import { processAmendment } from '../../shared/amendmentHelpers';
import { getPlanNumber } from './planHelpers';

/**
 * Process plan change amendment
 * @param {Object} quotationPayload - The new quotation data
 * @param {Object} policy - Current policy object
 * @param {String} newNuclearPlanCode - New nuclear plan code
 * @param {String} newParentPlanCode - New parent plan code
 * @param {Function} updateStatus - Callback to update loading status
 * @returns {Promise} - Resolves when amendment is complete
 */
export const processChangePlanAmendment = async (
  quotationPayload,
  policy,
  newNuclearPlanCode,
  newParentPlanCode,
  updateStatus
) => {
  try {
    // Get current plan codes
    const oldNuclearPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.primary_plan
      : null;
    const oldParentPlanCode = policy.quotation && policy.quotation.additional_details
      ? policy.quotation.additional_details.secondary_plan
      : null;

    const oldNuclearPlanNum = getPlanNumber(oldNuclearPlanCode);
    const oldParentPlanNum = getPlanNumber(oldParentPlanCode);
    const newNuclearPlanNum = getPlanNumber(newNuclearPlanCode);
    const newParentPlanNum = getPlanNumber(newParentPlanCode);

    // Calculate premium details
    const oldPremium = policy.quotation.policy_schedule_entries
      .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;
    const newPremium = quotationPayload.policy_schedule_entries
      .reduce((sum, entry) => sum + (entry.premium_amount_in_cents || 0), 0) / 100;
    const premiumDifference = newPremium - oldPremium;
    const changeType = premiumDifference > 0 ? 'Increase' : (premiumDifference < 0 ? 'Decrease' : 'No Change');

    // Build concise policy note
    const noteContent = `PLAN CHANGE AMENDMENT

Policy: ${policy.policy_number}

Plans:
Nuclear: Plan ${oldNuclearPlanNum} -> Plan ${newNuclearPlanNum}
Parent: ${oldParentPlanNum ? `Plan ${oldParentPlanNum} -> Plan ${newParentPlanNum}` : `Plan ${newParentPlanNum} (new)`}

Premium:
Old: BWP ${oldPremium.toFixed(2)}
New: BWP ${newPremium.toFixed(2)}
${changeType}: BWP ${Math.abs(premiumDifference).toFixed(2)}

Members: ${quotationPayload.policy_schedule_entries.length}`;

    // Use shared amendment processing (handles sanitization and length validation)
    return await processAmendment({
      quotationPayload,
      policy,
      noteContent,
      updateStatus
    });
  } catch (error) {
    console.error('Plan change amendment error:', error);
    throw new Error(
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'Failed to process plan change. Please try again.'
    );
  }
};
